import { useCallback } from 'react';

import { MediaPlans } from '@/api/MediaPlans';
import {
  ArtworkLink,
  ArtworkMatchFilesResponse,
  AttachmentFilesRequest,
  AttachmentResponse,
  AuditResponse,
  BookingRequest,
  CommentMessageRequest,
  CommentRequestDataWithFiles,
  CommentResponse,
  MediaPlan,
  MediaPlanAppliedFilters,
  MediaPlanFilterResponse,
  MediaPlanResponse,
  MediaPlanWithFiles,
  MediaPlansRequest,
  PackageBookingRequest,
  PageResponseMediaPlanResponse,
  PostgresFilePointer,
  SpaceSupportFileResponse,
  UpdateBooking,
  UpdateMediaPlanBookingSellingPricesRequest,
  UpdateMediaPlanBookingsServicesRequest,
  UpdateMultipleBookingsAvailability,
  UpdatePackageBooking,
  VisualResourcesRequest,
  VisualResourcesResponse,
} from '@/api/data-contracts';
import { ContentType } from '@/api/http-client';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

const useMediaPlansService = () => {
  const mediaPlansApi = useUploadProgressService(MediaPlans);

  const getMediaPlans = async (
    query: {
      request: MediaPlansRequest;
    },
    signal: AbortSignal | undefined,
  ): Promise<PageResponseMediaPlanResponse> => {
    const { data } = await mediaPlansApi.listPlans(query.request, {
      signal,
    });

    return data;
  };

  const getSpaceSupportFile = async (): Promise<SpaceSupportFileResponse> => {
    const { data } = await mediaPlansApi.getMediaPlansSpaceSupportFile();
    return data;
  };

  const getMediaPlanDetails = async (
    planId: string,
    signal: AbortSignal | undefined,
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.getMediaPlanById(planId, { signal });

    return data;
  };

  const getMediaPlanFilterValues = async (
    signal?: AbortSignal | undefined,
  ): Promise<MediaPlanFilterResponse> => {
    const { data } = await mediaPlansApi.getMediaPlanFilterValues({ signal });
    return data;
  };

  const createMediaPlan = async (plan: MediaPlanWithFiles): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.addMediaPlan(plan, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return data;
  };

  const updateMediaPlan = async (planId: string, plan: MediaPlan): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.updateMediaPlan(planId, plan, {});
    return data;
  };

  const deleteMediaPlan = async (planId: string): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.deleteMediaPlan(planId);
    return data;
  };

  const deleteBookings = async (
    planId: string,
    bookingIds: string[],
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.deleteMediaPlanBookings(planId, { bookingIds });
    return data;
  };

  const deletePackageBookings = async (
    planId: string,
    packageBookingIds: string[],
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.deleteMediaPlanPackageBookings(planId, {
      packageBookingIds,
    });
    return data;
  };

  const deleteBooking = async (planId: string, bookingId: string): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.deleteMediaPlanBooking(planId, bookingId, {
      headers: { 'Content-Type': 'application/json' },
      // @ts-ignore
      body: {},
    });
    return data;
  };

  const deletePackageBooking = async (
    planId: string,
    bookingId: string,
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.deleteMediaPlanPackageBooking(planId, bookingId, {
      headers: { 'Content-Type': 'application/json' },
      // @ts-ignore
      body: {},
    });
    return data;
  };

  const deleteDocument = async (planId: string, documentId: string): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.deleteMediaPlanDocument(planId, documentId, {
      headers: { 'Content-Type': 'application/json' },
      // @ts-ignore
      body: {},
    });
    return data;
  };

  const downloadMediaPlanDocumentById = useCallback(
    async (planId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await mediaPlansApi.downloadMediaPlanDocument(planId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [mediaPlansApi],
  );

  const getAttachmentsWithVersions = async (resourceId: string): Promise<object[]> => {
    const { data } = await mediaPlansApi.getAttachmentsWithVersions3(resourceId);
    return data;
  };

  const addBookingsToMediaPlan = async (
    planId: string,
    bookings: BookingRequest[],
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.mediaPlanAddBookings(planId, { bookings });
    return data;
  };

  const addPackageBookingsToMediaPlan = async (
    planId: string,
    packageBookings: PackageBookingRequest[],
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.mediaPlanAddPackageBookings(planId, { packageBookings });
    return data;
  };

  const updateMediaPlanDocuments = async (
    planId: string,
    keyVisuals: PostgresFilePointer[],
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.updateMediaPlanDocuments(
      planId,
      { keyVisuals },
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  };

  const updateMediaPlanBooking = async (
    planId: string,
    bookingId: string,
    form: UpdateBooking,
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.updateMediaPlanBooking(planId, bookingId, form);
    return data;
  };

  const updateMultipleBookingsAvailability = async (
    planId: string,
    payload: UpdateMultipleBookingsAvailability,
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.updateMediaPlanBookingsAvailability(planId, payload);
    return data;
  };

  const updateMediaPlanPackageBooking = async (
    planId: string,
    bookingId: string,
    request: UpdatePackageBooking,
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.updateMediaPlanPackageBooking(planId, bookingId, request);
    return data;
  };

  const addComment = async (
    resourceId: string,
    request: CommentRequestDataWithFiles,
  ): Promise<CommentResponse> => {
    const { data } = await mediaPlansApi.addComment1(resourceId, request);
    return data;
  };

  const editComment = async (
    resourceId: string,
    request: CommentMessageRequest,
  ): Promise<CommentResponse> => {
    const { data } = await mediaPlansApi.updateCommentMessage1(resourceId, request);
    return data;
  };

  const deleteComment = async (resourceId: string): Promise<void> => {
    await mediaPlansApi.deleteComment1(resourceId);
  };

  const downloadMediaPlanCommentDocument = useCallback(
    async (resourceId: string, documentId: string, signal?: AbortSignal): Promise<Blob> => {
      const { data } = await mediaPlansApi.downloadCommentInlineDocument1(resourceId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [mediaPlansApi],
  );

  const getComments = async (
    resourceId: string,
    signal: AbortSignal | undefined,
  ): Promise<Record<string, CommentResponse[]>> => {
    const { data } = await mediaPlansApi.getComments1(resourceId, { signal });
    return data;
  };

  const uploadMediaPlanAttachmentVersion = useCallback(
    async (
      mediaPlanId: string,
      attachmentId: string,
      category: string,
      request: AttachmentFilesRequest,
    ): Promise<AttachmentResponse> => {
      const { data } = await mediaPlansApi.updateNewVersion2(
        mediaPlanId,
        category,
        attachmentId,
        {},
        { body: request, type: ContentType.FormData },
      );
      return data;
    },
    [mediaPlansApi],
  );

  const setAttachmentActiveVersion = useCallback(
    async (
      mediaPlanId: string,
      attachmentId: string,
      versionId: number,
    ): Promise<AttachmentResponse> => {
      const { data } = await mediaPlansApi.setActiveVersion2(mediaPlanId, attachmentId, versionId);
      return data;
    },
    [mediaPlansApi],
  );

  const deleteAttachmentVersion = useCallback(
    async (
      mediaPlanId: string,
      attachmentId: string,
      versionId: number,
    ): Promise<AttachmentResponse> => {
      const { data } = await mediaPlansApi.deleteVersion2(mediaPlanId, attachmentId, versionId);
      return data;
    },
    [mediaPlansApi],
  );

  const getMediaPlanFilters = async (
    mediaPlanId: string,
    signal?: AbortSignal | undefined,
  ): Promise<MediaPlanAppliedFilters> => {
    const { data } = await mediaPlansApi.getMediaPlanFilters(mediaPlanId, { signal });
    const spaceFilters = Object.fromEntries(
      Object.entries(data.spaceFilters ?? {}).map(([key, value]) => [key, value ?? undefined]),
    );
    const packageFilters = Object.fromEntries(
      Object.entries(data.packageFilters ?? {}).map(([key, value]) => [key, value ?? undefined]),
    );

    return {
      // @ts-ignore
      spaceFilters,
      // @ts-ignore
      packageFilters,
    };
  };

  const updateMediaPlanFilters = async (
    mediaPlanId: string,
    request: MediaPlanAppliedFilters,
    signal?: AbortSignal | undefined,
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.updateMediaPlanFilters(mediaPlanId, request, { signal });
    return data;
  };

  const deleteArtworkDocument = async (
    planId: string,
    bookingId: string,
    documentId: string,
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.deleteMediaPlanArtworkDocument(
      planId,
      bookingId,
      documentId,
      {
        headers: { 'Content-Type': 'application/json' },
        // @ts-ignore
        body: {},
      },
    );
    return data;
  };

  const updateMediaPlanBookingArtworkLinks = async (
    planId: string,
    bookingId: string,
    payload: ArtworkLink[],
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.updateMediaPlanBookingArtWorkLinks(
      planId,
      bookingId,
      payload,
    );
    return data;
  };

  const updateMediaPlanBookingsServices = async (
    planId: string,
    payload: UpdateMediaPlanBookingsServicesRequest,
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.updateMediaPlanBookingsServices(planId, payload);
    return data;
  };

  const updateMediaPlanBookingSellingPrices = async (
    planId: string,
    payload: UpdateMediaPlanBookingSellingPricesRequest,
  ) => {
    return mediaPlansApi.updateMediaPlanBookingSellingPrices(planId, payload);
  };

  const previewMediaPlanBookingSellingPrices = async (
    planId: string,
    payload: UpdateMediaPlanBookingSellingPricesRequest,
  ): Promise<MediaPlanResponse> => {
    const { data } = await mediaPlansApi.previewMediaPlanBookingSellingPrices(planId, payload);
    return data;
  };

  const uploadMediaPlanBookingArtworkMatchFiles = useCallback(
    async (
      planId: string,
      bookingId: string,
      files: File[],
    ): Promise<ArtworkMatchFilesResponse> => {
      const formData = new FormData();
      files.forEach((file) => formData.append('artworkMatchFiles', file));
      const { data } = await mediaPlansApi.uploadMediaPlanBookingArtworkMatchFiles(
        planId,
        bookingId,
        formData as any,
      );
      return data;
    },
    [mediaPlansApi],
  );

  const deleteMediaPlanBookingArtworkMatchFile = useCallback(
    async (
      planId: string,
      bookingId: string,
      documentId: string,
    ): Promise<ArtworkMatchFilesResponse> => {
      const { data } = await mediaPlansApi.deleteMediaPlanBookingArtworkMatchFile(
        planId,
        bookingId,
        documentId,
      );
      return data;
    },
    [mediaPlansApi],
  );

  const downloadMediaPlanBookingArtworkMatchFile = useCallback(
    async (
      planId: string,
      bookingId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const response = await mediaPlansApi.downloadArtworkMatchFile2(
        planId,
        bookingId,
        documentId,
        { format: 'blob', signal } as any,
      );
      return response.data;
    },
    [mediaPlansApi],
  );

  const downloadMediaPlanBookingArtworkMatchThumbnail = useCallback(
    async (planId: string, bookingId: string, documentId: string): Promise<Blob> => {
      const response = await mediaPlansApi.getMediaPlanBookingArtworkMatchThumbnail(
        planId,
        bookingId,
        documentId,
      );
      return response.data;
    },
    [mediaPlansApi],
  );

  const getMediaPlanVisualResources = useCallback(
    async (planId: string, signal: AbortSignal | undefined): Promise<VisualResourcesResponse> => {
      const { data } = await mediaPlansApi.getMediaPlanVisualResources(planId, { signal });
      return data;
    },
    [mediaPlansApi],
  );

  const updateMediaPlanVisualResources = useCallback(
    async (planId: string, request: VisualResourcesRequest): Promise<VisualResourcesResponse> => {
      const { data } = await mediaPlansApi.patchMediaPlanVisualResources(planId, request);
      return data;
    },
    [mediaPlansApi],
  );

  const getMediaPlanHistory = useCallback(
    async (mediaPlanId: string): Promise<AuditResponse[]> => {
      const { data } = await mediaPlansApi.getMediaPlanHistory(mediaPlanId);
      return data;
    },
    [mediaPlansApi],
  );

  return {
    getMediaPlans,
    getMediaPlanDetails,
    updateMediaPlan,
    createMediaPlan,
    deleteMediaPlan,
    deleteBooking,
    deleteBookings,
    deleteDocument,
    downloadMediaPlanDocumentById,
    getAttachmentsWithVersions,
    addBookingsToMediaPlan,
    updateMediaPlanDocuments,
    updateMediaPlanBooking,
    addComment,
    editComment,
    deleteComment,
    downloadMediaPlanCommentDocument,
    getComments,
    uploadMediaPlanAttachmentVersion,
    setAttachmentActiveVersion,
    deleteAttachmentVersion,
    addPackageBookingsToMediaPlan,
    updateMediaPlanPackageBooking,
    deletePackageBooking,
    deletePackageBookings,
    getMediaPlanFilters,
    updateMediaPlanFilters,
    getMediaPlanFilterValues,
    deleteArtworkDocument,
    updateMediaPlanBookingArtworkLinks,
    getSpaceSupportFile,
    updateMultipleBookingsAvailability,
    updateMediaPlanBookingsServices,
    updateMediaPlanBookingSellingPrices,
    previewMediaPlanBookingSellingPrices,
    uploadMediaPlanBookingArtworkMatchFiles,
    deleteMediaPlanBookingArtworkMatchFile,
    downloadMediaPlanBookingArtworkMatchFile,
    downloadMediaPlanBookingArtworkMatchThumbnail,
    getMediaPlanVisualResources,
    updateMediaPlanVisualResources,
    getMediaPlanHistory,
  };
};

export default useMediaPlansService;
